import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Space } from "@mantine/core";
import {
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jem-open/jem-ui";
import Tag from "../services/model/Tag";
import ErrorPage from "../../../common/pages/ErrorPage";
import Pagination from "../../../themes/components/Pagination";
import useTagCategoriesApiService from "../services/api/TagCategoriesApiService";
import useTagsPaginatedApiService from "../services/api/TagsPaginatedApiService";
import TagTableActionMenu from "../elements/TagTableActionMenu";
import EmployeeCountBadge from "./EmployeeCountBadge";
import { AuthContext } from "../../../providers/AuthProvider";
import TagTableEmptyStateContainer from "./TagTableEmptyStateContainer";
import SkeletonRows from "./SkeletonRows";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";

const DEFAULT_PAGE_SIZE = 40;
const ALL_CATEGORIES = "__all__";

type TagsTableProps = {
  selection: Tag[];
  onSelection: React.Dispatch<React.SetStateAction<Tag[]>>;
};

const TagsTable: React.FC<TagsTableProps> = ({ selection, onSelection }) => {
  const auth = useContext(AuthContext);
  const tableMaxHeight = ResponsiveHeight(
    "",
    "70vh",
    "70vh",
    "70vh",
    "56vh",
    "70vh"
  );

  const [search, setSearch] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [activePage, setActivePage] = useState(1);

  const { data: categoriesData } = useTagCategoriesApiService();

  const { isError, isLoading, isFetching, data: apiData, error } =
    useTagsPaginatedApiService({
      search,
      page: activePage,
      page_size: DEFAULT_PAGE_SIZE,
      categories: selectedCategory ?? undefined,
    });

  const categories = categoriesData ?? [];

  const tags = apiData?.results ?? [];

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchKeyword);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  useEffect(() => {
    setActivePage(1);
  }, [search, selectedCategory]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  const isEmpty =
    !isLoading &&
    !isFetching &&
    (!apiData || (!apiData.results.length && !search.length));
  const showCategoryColumn = selectedCategory === null;
  const headers = showCategoryColumn
    ? ["Tag name", "Category", "Employees", ""]
    : ["Tag name", "Employees", ""];
  const columnCount = showCategoryColumn ? 4 : 3;

  return (
    <>
      <Space h="xl" />
      <div className="flex gap-sm">
        <Select
          value={selectedCategory ?? ALL_CATEGORIES}
          onValueChange={(val) =>
            setSelectedCategory(val === ALL_CATEGORIES ? null : val)
          }
        >
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CATEGORIES}>All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="w-[300px]">
          <SearchInput
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onClear={() => {
              setSearchKeyword("");
              setSearch("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") setSearch(searchKeyword);
            }}
            placeholder="Search..."
          />
        </div>
      </div>
      <Space h="md" />
      {isEmpty ? (
        <TagTableEmptyStateContainer />
      ) : (
        <DirectoryTable<Tag>
          loadingRows={
            isLoading || isFetching ? (
              <SkeletonRows columnCount={columnCount} />
            ) : undefined
          }
          hideSelectControls
          tableMaxHeight={tableMaxHeight}
          data={tags}
          selection={selection}
          onSelect={onSelection}
          headers={headers}
          rows={tags.map((it) => ({
            name: it.name,
            ...(showCategoryColumn && { category: it.category }),
            employees: (
              <EmployeeCountBadge
                count={it.employees ? it.employees.length : 0}
                href={
                  it.employees && it.employees.length > 0
                    ? `/employees?tag=${it.id}#users`
                    : undefined
                }
              />
            ),
            actions: (
              <div className="flex justify-end">
                <TagTableActionMenu id={it.id} />
              </div>
            ),
          }))}
        />
      )}
      {apiData && apiData.pages > 1 && (
        // Offset to prevent overlap with the Jem Employer Insights chat logo
        <div style={{ marginRight: "50px" }}>
          <Pagination
            onChange={setActivePage}
            total={apiData.pages}
            initialPage={1}
          />
        </div>
      )}
    </>
  );
};

export default TagsTable;
